import type { ResolvedAssets, VideoFormat, VideoSpec } from './types';

export const FPS = 30;
/** Cross-fade overlap between consecutive scenes, in frames. */
export const OVERLAP = 12;
/** Narration starts this many frames into its scene. */
export const VOICE_DELAY = 10;

export const SIZES: Record<VideoFormat, { width: number; height: number }> = {
    landscape: { width: 1920, height: 1080 },
    portrait: { width: 1080, height: 1920 },
    square: { width: 1080, height: 1080 },
};

export interface TimedScene {
    index: number;
    from: number;
    frames: number;
    /** Not in the video (an end card while spec.endCard is off): frames = 0. */
    skipped?: boolean;
}

/** Whether scene `i` is part of the video (end cards can be switched off). */
export const sceneIncluded = (spec: VideoSpec, i: number) => !(spec.scenes[i]?.type === 'cta' && spec.endCard?.enabled === false);

/** Frame layout of every scene; scenes stretch to fit their narration. */
export function computeTimeline(spec: VideoSpec, assets?: ResolvedAssets) {
    const scenes: TimedScene[] = [];
    let cursor = 0;
    let shown = 0;
    spec.scenes.forEach((s, i) => {
        // Keep one entry per spec scene so indexes line up with spec.scenes.
        if (!sceneIncluded(spec, i)) {
            scenes.push({ index: i, from: Math.max(0, cursor - OVERLAP), frames: 0, skipped: true });
            return;
        }
        const voice = spec.voice.enabled ? assets?.voice?.[s.id] : undefined;
        const minFrames = voice ? Math.ceil(voice.duration * FPS) + VOICE_DELAY + 18 : 0;
        const frames = Math.max(Math.round(s.duration * FPS), minFrames, OVERLAP * 3);
        const from = shown === 0 ? 0 : cursor - OVERLAP;
        shown++;
        scenes.push({ index: i, from, frames });
        cursor = from + frames;
    });
    return { scenes, durationInFrames: Math.max(cursor, FPS), ...SIZES[spec.format] };
}
